<template lang="pug">
div.commentary-group-outer
  ErrorAlert(
    v-if="headingError"
    :message="headingError.message"
    :stack="headingError.stack"
    @retry="headingQuery.refetch"
    @report="() => report(headingError)"
  )
  h2.section-title-2(
    v-else
    v-html="heading ?? '…'"
  )

  ErrorAlert(
    v-if="verseError"
    :message="verseError.message"
    :stack="verseError.stack"
    @retry="verseQuery.refetch"
    @report="() => report(verseError)"
  )
  VvBlockQuote(v-else-if="verseQuote || verseQuery.isPending.value")
    VSkeletonLoader(
      v-if="verseQuery.isPending.value"
      type="paragraph"
    )
    p.text-body-1(
      v-else
      v-html="verseQuote"
    )

  div.commentary-doc.my-2(
    v-for="doc in docs"
    :key="doc.nid"
  )
    CommentTeaserCard(
      v-if="doc.comment"
      :heading="doc.comment.heading"
      :teaser="doc.comment.teaser"
      :ranges="doc.comment.ranges"
      :detail-level-name="doc.comment.detailLevel.name[locale] ?? '?'"
      :detail-level-num-dots="numDotsPerDetailLevelWeight[doc.comment.detailLevel.weight] ?? 0"
      :commentary-type-machine-name="commentaryTypeMachineName"
      :nid="doc.nid"
      :local-tid="localTid"
      :is-selected="selected.includes(doc.nid)"
      :show-default-reading-list="true"
      :show-reading-list-menu="true"
      :language="doc.comment.language"
    )
    CommentaryImage(
      v-else-if="doc.image"
      :title="doc.image.title"
      :description="doc.image.description"
      :large-url="doc.image.styles.large"
      :small-url="doc.image.styles.small"
      :placeholder-data="doc.image.styles.placeholder"
      :original-url="doc.image.styles.original"
    )
  div.hr(v-if="subGroupComputed.subGroups.length > 0 && verseQuote")

  CommentaryGroupInner(
    v-for="group in subGroupComputed.subGroups"
    :key="JSON.stringify([group.range, group.verse, group.versePart])"
    :range="group.range"
    :verse="group.verse"
    :verse-part="group.versePart"
    :doclist="group.doclist"
    :local-tid="localTid"
    :commentary-type-machine-name="commentaryTypeMachineName"
    :selected="selected"
    :translations-by-nid="subGroupComputed.otherCommentsByNid"
  )
</template>

<script setup lang="ts">
import {
  createVerse,
  dumpRange,
  formatVerseText,
  getRangeFirstVerse,
  isSingleVerse,
  renderRangeReference,
  withTranslation,
} from "~/units/scripture";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { okAsync } from "neverthrow";
import { useQuery } from "@tanstack/vue-query";
import type { StandaloneVerseData } from "@rsc/scripture-model";
import { injectRequired } from "~/injectRequired";
import { LogFunctionKey, ScriptureClientKey } from "~/injectionKeys";
import { numDotsPerDetailLevelWeight } from "~/static/taxonomy/detailLevels";
import type { Props } from "~/components/CommentaryGroupOuter.types";
import { makeResultAsyncSerializable } from "~/errors/util";
import {
  computeDocList,
  type ComputedSubGroups,
  computeSubGroups,
  type DocComputed,
} from "./CommentaryGroup.util";
import { useReport } from "~/logging/report";

const report = useReport();

const i18n = useI18n();
const locale = i18n.locale;

const scriptureClient = injectRequired(ScriptureClientKey);
const log = injectRequired(LogFunctionKey);

const props = withDefaults(defineProps<Props>(), {
  subGroups: () => [],
  selected: () => [],
});

const verseQuery = useQuery({
  queryKey: [
    "CommentaryGroupOuter",
    "verse",
    computed(() => (props.range == null ? undefined : dumpRange(props.range))),
    computed(() => props.localTid),
  ],
  queryFn: () =>
    makeResultAsyncSerializable(() => {
      if (!props.range) {
        // We do not have a specific verse. There is probably an explicit heading.
        return okAsync(undefined);
      }

      if (isSingleVerse(props.range)) {
        // This is an outer group for a single verse.
        return getRangeFirstVerse(
          withTranslation(props.range, props.range?.tid || props.localTid),
          scriptureClient,
        ).map((data) => data ?? undefined);
      }

      // This is an outer group for a range of verses (or a compound verse).
      return okAsync(undefined);
    }, log),
});

const verseData = computed<StandaloneVerseData | undefined>(
  () => verseQuery.data.value?.value,
);

const verseError = computed(
  () => verseQuery.error.value ?? verseQuery.data.value?.error,
);

const verseQuote = computed<string>(() =>
  verseData.value ? formatVerseText(createVerse(verseData.value)) : "",
);

const headingQuery = useQuery({
  queryKey: [
    "CommentaryGroupOuter",
    "heading",
    computed(() => props.heading),
    computed(() => (props.range == null ? undefined : dumpRange(props.range))),
    computed(() => props.localTid),
  ],
  queryFn: () =>
    makeResultAsyncSerializable(() => {
      if (props.heading) {
        return okAsync(props.heading);
      }

      if (props.range) {
        return renderRangeReference(
          props.range,
          scriptureClient,
          props.localTid,
          !!props.range.tid,
          false,
        );
      }

      return okAsync("General");
    }, log),
});

const heading = computed<string | undefined>(
  () => headingQuery.data.value?.value,
);

const headingError = computed(
  () => headingQuery.error.value ?? headingQuery.data.value?.error,
);

const docs = computed<DocComputed[]>(() => {
  return computeDocList(props.doclist.docs);
});

const subGroupComputed = computed<ComputedSubGroups>(() => {
  return computeSubGroups(toRaw(props.subGroups));
});
</script>
